import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Sparkles, BarChart3 } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      if (mode === "register" || data?.pending) {
        setNotice(data?.message || "Account created. An administrator must approve your access before you can sign in.");
        setMode("login");
        setForm({ name: "", email: form.email, password: "" });
      } else {
        setUser(data);
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative flex-col justify-between p-12 text-white overflow-hidden"
        style={{ background: "linear-gradient(150deg, hsl(184 66% 13%) 0%, hsl(184 62% 19%) 55%, hsl(186 55% 24%) 100%)" }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full" style={{ background: "hsl(var(--gold) / 0.12)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white rounded-lg px-3 py-2 inline-flex shadow-pop"><img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_tag-candidate-portal/artifacts/swk6w00s_IMG_9395.png" alt="The Allicock Group" className="h-8 w-auto object-contain" /></div>
          <span className="font-head font-bold text-xl tracking-tight">TAG Recruit AI</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-head text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1]">Precision talent matching for workforce solutions.</h1>
          <p className="mt-5 text-white/75 text-base leading-relaxed">Parse résumés, score candidates against every open position, and surface explainable, defensible recommendations — the credible way.</p>
          <div className="mt-9 space-y-3.5">
            {[[Sparkles, "AI résumé parsing & explainable match scoring"], [BarChart3, "Per-position candidate buckets & analytics"], [ShieldCheck, "Private, invite-only, admin-approved access"]].map(([Icon, text], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-white/85">
                <span className="h-8 w-8 rounded-md bg-white/10 flex items-center justify-center shrink-0"><Icon size={16} className="text-[hsl(var(--gold))]" /></span>
                {text}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-white/50 text-xs">Internal recruiter platform · The Allicock Group</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_tag-candidate-portal/artifacts/swk6w00s_IMG_9395.png" alt="The Allicock Group" className="h-9 w-auto object-contain" />
            <span className="font-head font-extrabold text-lg">TAG Recruit AI</span>
          </div>
          <h2 className="font-head text-2xl font-bold tracking-tight">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Sign in to access the recruiter workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5" />
            </div>
            {notice && <div data-testid="auth-notice" className="text-sm rounded-md border border-[hsl(var(--teal))]/30 bg-[hsl(var(--teal))]/8 text-[hsl(var(--teal))] px-3 py-2">{notice}</div>}
            {error && <div data-testid="auth-error" className="text-sm text-destructive">{error}</div>}
            <Button type="submit" disabled={busy} data-testid="auth-submit" className="w-full bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" onClick={googleLogin} data-testid="google-login" className="w-full">Continue with Google</Button>

          <p className="text-sm text-center text-muted-foreground mt-6">
            {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
            <button data-testid="toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNotice(""); }} className="text-[hsl(var(--teal))] font-medium hover:underline">
              {mode === "login" ? "Register" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
